// Reads the Cohere Launcher origin flag that the auth app writes to sessionStorage
// (packages/auth/src/util/queryParams.ts -> setLauncherContext). TrackUsage lives in
// packages/common and must not import from packages/auth, so the key/origin literals are
// duplicated here and MUST stay in sync with queryParams.ts.
//
// RW-747: when the launcher origin is active, the shared tracker emits COHERE_LAUNCHER
// trackers. Tenancy (healthPlanName/delegatedVendorName) is carried on the context so the
// tracker can (a) evaluate the enableTimeTracker LaunchDarkly flag and (b) populate the
// tracker body without a ServiceCase. RW-748 (landing page) is responsible for writing them.

export const LAUNCHER_SESSION_KEY = "cohereLauncherContext";
export const COHERE_LAUNCHER_ORIGIN = "cohereLauncher";

export interface LauncherTrackingContext {
  origin?: string;
  healthPlanName?: string;
  delegatedVendorName?: string;
  // The specific launcher action label (e.g. "MD clinical review"); used as the tracker component
  // so launcher reviews aren't collapsed to the route's default "Clinical Review".
  component?: string;
  // RW-748: the server-generated uniqueIdentifier of the launcher tracker created for this
  // session, written back on create so a later step (RW-749) can PATCH attachIdentifiers.
  uniqueIdentifier?: string;
  // RW-747: the enableTimeTracker decision, evaluated once on the landing page (which lives inside
  // the RestfulProvider/LaunchDarkly providers) and stored here. TrackRoute renders outside those
  // providers and cannot evaluate the flag, so it reads this instead. Kept in sync with the auth
  // LauncherContext (packages/auth/src/util/queryParams.ts).
  trackingEnabled?: boolean;
}

export function getLauncherTrackingContext(): LauncherTrackingContext | null {
  try {
    const raw = sessionStorage.getItem(LAUNCHER_SESSION_KEY);
    return raw ? (JSON.parse(raw) as LauncherTrackingContext) : null;
  } catch {
    return null;
  }
}

export function isLauncherOriginActive(): boolean {
  return getLauncherTrackingContext()?.origin === COHERE_LAUNCHER_ORIGIN;
}

/**
 * RW-748: merge the created tracker's uniqueIdentifier into the existing launcher context so a
 * later completion step (RW-749) can attach late-bound ids. No-op if the context is absent.
 */
export function setLauncherTrackerUniqueIdentifier(uniqueIdentifier: string): void {
  try {
    const raw = sessionStorage.getItem(LAUNCHER_SESSION_KEY);
    if (!raw) {
      return;
    }
    const ctx = JSON.parse(raw) as LauncherTrackingContext;
    ctx.uniqueIdentifier = uniqueIdentifier;
    sessionStorage.setItem(LAUNCHER_SESSION_KEY, JSON.stringify(ctx));
  } catch {
    // best-effort — never block tracking on a storage failure
  }
}
